import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { createColumnHelper } from '@tanstack/react-table';
import { Order, OrderItem, RelatedTopup } from 'models/Order';
import { completeOrderPaymentAPI, getOrderByIdAPI, getOrderRelatedTopupsAPI } from 'services/ordersApi';
import { DashboardSkeleton } from 'components/Skeleton/DashboardSkeleton';
import { CustomCard } from 'components/CustomCard';
import { toastSuccess, toastWarning } from 'utils/toastMethod';
import CustomTable from 'components/Table/CustomTable';
import PageLayout from 'components/Layout/PageLayout';
import PageLayoutSkeleton from 'components/Layout/PageLayout/skeleton';

const currencyFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const formatCurrency = (cents: number) => currencyFormatter.format(cents / 100);

const formatCoverageStatus = (status: string) => {
  if (status === 'COVERED') return 'Coperto';
  if (status === 'UNCOVERED') return 'Non coperto';
  return status;
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', marginBottom: '8px' }}>
    <div style={{ minWidth: '180px', fontWeight: 600, color: '#202020' }}>{label}</div>
    <div style={{ minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

export default function OrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | undefined>();
  const [relatedTopups, setRelatedTopups] = useState<RelatedTopup[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setNotFound(true);
      return;
    }
    getOrderByIdAPI(orderId)
      .then((found: Order) => {
        if (found) setOrder(found);
        else setNotFound(true);
      })
      .catch(() => {
        setNotFound(true);
        toastWarning('Qualcosa è andato storto!');
      });
    getOrderRelatedTopupsAPI(orderId)
      .then((topups: RelatedTopup[]) => setRelatedTopups(topups ?? []))
      .catch(() => toastWarning('Impossibile recuperare le ricariche collegate.'));
  }, [orderId]);

  const canConfirmPayment =
    !!order?.payment && order.payment.method === 'Manuale' && order.payment.status === 'In attesa';

  const confirmPayment = () => {
    if (!order?.payment) return;
    setIsConfirmingPayment(true);
    completeOrderPaymentAPI(order.id, order.payment.id, {
      amountPaid: order.payment.amount,
      amountDiscount: order.payment.discount,
      status: 'PAID',
    })
      .then(() => {
        toastSuccess('Pagamento confermato correttamente.');
        setOrder((prev) => (prev?.payment ? { ...prev, payment: { ...prev.payment, status: 'Pagato' } } : prev));
      })
      .catch(() => toastWarning('Qualcosa è andato storto!'))
      .finally(() => {
        setIsConfirmingPayment(false);
        setShowConfirmModal(false);
      });
  };

  const columnHelper = createColumnHelper<OrderItem>();
  const itemColumns = [
    columnHelper.accessor('name', {
      header: () => 'NOME',
      cell: (name) => name.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('description', {
      header: () => 'DESCRIZIONE',
      cell: (description) => description.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('quantity', {
      header: () => 'QUANTITÀ',
      cell: (quantity) => quantity.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('price', {
      header: () => 'PREZZO',
      cell: (price) => formatCurrency(price.getValue() as number),
      enableColumnFilter: false,
      enableSorting: false,
    }),
  ];

  const topupColumnHelper = createColumnHelper<RelatedTopup>();
  const topupColumns = [
    topupColumnHelper.accessor('topUpId', {
      header: () => 'ID RICARICA',
      cell: (topUpId) => topUpId.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    topupColumnHelper.accessor('status', {
      header: () => 'STATO',
      cell: (status) => status.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    topupColumnHelper.accessor('createdAt', {
      header: () => 'DATA',
      cell: (createdAt) => formatDate(createdAt.getValue() as string),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    topupColumnHelper.accessor('totalAmount', {
      header: () => 'IMPORTO TOTALE',
      cell: (totalAmount) => formatCurrency(totalAmount.getValue() as number),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    topupColumnHelper.accessor('allocatedAmount', {
      header: () => 'IMPORTO ALLOCATO',
      cell: (allocatedAmount) => formatCurrency(allocatedAmount.getValue() as number),
      enableColumnFilter: false,
      enableSorting: false,
    }),
  ];

  if (notFound) {
    return (
      <PageLayout titlePage="Dettaglio ordine">
        <h4>Ordine non trovato</h4>
        <button className="btn" style={{ cursor: 'pointer' }} onClick={() => navigate('/orders')}>
          Torna alla lista ordini
        </button>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayoutSkeleton>
        <DashboardSkeleton />
      </PageLayoutSkeleton>
    );
  }

  return (
    <PageLayout
      titlePage={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => navigate('/orders')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: '28px',
                fontWeight: 600,
                lineHeight: 1,
              }}
              aria-label="Torna alla lista ordini"
            >
              &#8592;
            </button>
            <span>Dettaglio ordine</span>
          </div>
          <button
            className="btn btn-primary"
            style={{
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
            onClick={() => navigate(`/topups/new?orderId=${order.id}`)}
          >
            + Nuova ricarica
          </button>
        </div>
      }
      subtitlePage={`Ordine ${order.id}`}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
        <CustomCard title={<h3>Informazioni ordine</h3>} width="380px">
          <DetailRow label="Stato" value={order.status} />
          <DetailRow label="Totale" value={formatCurrency(order.total)} />
          <DetailRow label="Data ordine" value={formatDate(order.created)} />
          <DetailRow label="Ultimo aggiornamento" value={formatDate(order.lastUpdate)} />
        </CustomCard>
        <CustomCard title={<h3>Cliente</h3>} width="380px">
          <DetailRow label="Tipologia" value={order.businessBillingDetails ? 'Azienda' : 'Privato'} />
          <DetailRow label="Nome" value={order.customerName} />
          <DetailRow label="Email" value={order.customerEmail} />
        </CustomCard>
        {order.businessBillingDetails ? (
          <CustomCard title={<h3>Dati di fatturazione</h3>} width="380px">
            <DetailRow label="Ragione sociale" value={order.businessBillingDetails.companyName} />
            <DetailRow label="Indirizzo" value={order.businessBillingDetails.address} />
            <DetailRow label="CAP" value={order.businessBillingDetails.zipCode} />
            <DetailRow label="Città" value={order.businessBillingDetails.city} />
            <DetailRow label="Provincia" value={order.businessBillingDetails.province} />
            <DetailRow label="Paese" value={order.businessBillingDetails.country} />
            <DetailRow label="P. IVA" value={order.businessBillingDetails.vatNumber} />
            <DetailRow label="Codice fiscale" value={order.businessBillingDetails.taxCode} />
            <DetailRow label="Codice SDI" value={order.businessBillingDetails.sdiCode} />
            <DetailRow label="Email fatturazione" value={order.businessBillingDetails.billingEmail} />
          </CustomCard>
        ) : null}
        {order.payment ? (
          <CustomCard title={<h3>Pagamento</h3>} width="380px">
            <DetailRow label="Metodo" value={order.payment.method} />
            <DetailRow label="Tipo" value={order.payment.type} />
            <DetailRow label="Importo" value={formatCurrency(order.payment.amount)} />
            <DetailRow label="Sconto" value={formatCurrency(order.payment.discount)} />
            <DetailRow label="Stato" value={order.payment.status} />
            <DetailRow label="Copertura ricariche" value={formatCoverageStatus(order.paymentTopupCoverageStatus)} />
            {canConfirmPayment ? (
              <button
                className="btn btn-primary"
                style={{
                  cursor: isConfirmingPayment ? 'not-allowed' : 'pointer',
                  marginTop: '16px',
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  color: '#ffffff',
                }}
                disabled={isConfirmingPayment}
                onClick={() => setShowConfirmModal(true)}
              >
                {isConfirmingPayment ? 'Conferma in corso...' : 'Conferma pagamento'}
              </button>
            ) : null}
          </CustomCard>
        ) : null}
      </div>

      <h3>Articoli</h3>
      {order.items && order.items.length > 0 ? (
        <CustomTable
          data={order.items}
          columns={itemColumns}
          pageStartIndex={0}
          pageMaxSize={10}
          columnsVisibility={{}}
        />
      ) : (
        <h4>Nessun articolo presente</h4>
      )}

      <h3 style={{ marginTop: '24px' }}>Ricariche collegate</h3>
      {relatedTopups.length > 0 ? (
        <CustomTable
          data={relatedTopups}
          columns={topupColumns}
          pageStartIndex={0}
          pageMaxSize={10}
          columnsVisibility={{}}
        />
      ) : (
        <p>Nessuna ricarica collegata</p>
      )}

      <Modal
        show={showConfirmModal}
        onHide={() => (isConfirmingPayment ? undefined : setShowConfirmModal(false))}
        centered
      >
        <Modal.Header closeButton={!isConfirmingPayment}>
          <Modal.Title>Conferma pagamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>Sei sicuro di voler confermare il pagamento? Verranno generate le gift card.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isConfirmingPayment}>
            Annulla
          </Button>
          <Button variant="primary" onClick={confirmPayment} disabled={isConfirmingPayment}>
            {isConfirmingPayment ? 'Conferma in corso...' : 'Conferma'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
